import {FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {FixedSizeList as VirtualList} from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import {Avatar, Badge, Button, Input, Space, Spin, Tooltip, Typography} from "antd";
import {
    ArrowLeftOutlined,
    BankOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    InboxOutlined,
    SearchOutlined,
    SendOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
} from "@ant-design/icons";
import {useMutation, useQuery, useQueryClient, UseQueryResult} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router-dom";
import {formatDistanceToNow} from "date-fns";
import {de} from "date-fns/locale";
import styled from "styled-components";
import {useMediaQuery} from "@uidotdev/usehooks";

import {useMyMessages} from "./hooks/useMyMessages.tsx";
import {IMessage, IMessageBuble} from "../../services/types.ts";
import {
    getCandidateFromMsgByIdApi,
    getCandidateMessagesByComId,
    sendMessageApi,
    setConversationHidden,
} from "../../services/api.ts";
import {MessageItem} from "./components/MessageItem.tsx";
import {AbsLoader} from "../jobPool/JobPool.tsx";
import {colors} from "../../util/colors.ts";
import {AuthContext} from "../../context/AuthContext.tsx";
import {
    ApplicationStatus,
    getInitials,
    STATUS_CONFIG,
    STATUS_TABS,
} from "./constants.ts";

const {Text, Title} = Typography;
const {TextArea} = Input;

const LIMIT = 50;
const SIDEBAR_WIDTH = 400;
const INFO_PANEL_WIDTH = 300;
const ROW_HEIGHT = 124;

type StatusFilter = ApplicationStatus | "all";

type FirstMessageType = Omit<IMessage, "_candidate_2_0" | "_client" | "_job_demand_with_labels">;
type CombinedMessageType = IMessageBuble | FirstMessageType;

const deriveStatus = (item?: IMessage | null): ApplicationStatus => {
    if (!item) return "pending";
    if (!item._job_demand_with_labels?.[0]?.id) return "rejected";
    return "pending";
};

export const Messages: FC = () => {
    const {user} = useContext(AuthContext);
    const navigate = useNavigate();
    const {id: urlId} = useParams<{id: string}>();
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const queryClient = useQueryClient();

    const [showHidden, setShowHidden] = useState(false);

    const {
        data,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useMyMessages(LIMIT, showHidden);

    const listRef = useRef<VirtualList | null>(null);

    const flattenedData = useMemo<IMessage[]>(() => {
        if (!data || (data.pages.length === 1 && data.pages[0][0]?.id === 0)) return [];
        return data.pages.flatMap((page) => (Array.isArray(page) ? page : []));
    }, [data]);

    // ── Selection (synced with URL) ──────────────────────────────────────
    const [selectedId, setSelectedId] = useState<number | null>(() => {
        const n = urlId ? Number(urlId) : NaN;
        return Number.isFinite(n) ? n : null;
    });

    useEffect(() => {
        if (!urlId) return;
        const n = Number(urlId);
        if (Number.isFinite(n)) setSelectedId(n);
    }, [urlId]);

    // ── Search + status filter (UI-only) ─────────────────────────────────
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
    const [showInfo, setShowInfo] = useState(true);
    const [reply, setReply] = useState("");

    const filtered = useMemo(() => {
        let r = flattenedData;
        if (search.trim()) {
            const q = search.toLowerCase();
            r = r.filter(
                (m) =>
                    (m._client?.profile_name || "").toLowerCase().includes(q) ||
                    (m._job_demand_with_labels?.[0]?.title || "").toLowerCase().includes(q) ||
                    (m.message || "").toLowerCase().includes(q),
            );
        }
        if (statusFilter !== "all") {
            r = r.filter((m) => deriveStatus(m) === statusFilter);
        }
        return [...r].sort((a, b) =>
            sortOrder === "newest"
                ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                : new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
    }, [flattenedData, search, statusFilter, sortOrder]);

    // Auto-select the top conversation on desktop when none is selected.
    useEffect(() => {
        if (!isMobile && !selectedId && filtered.length > 0) {
            const first = filtered[0];
            setSelectedId(first.id);
            navigate(`/messages/${first.id}`, {replace: true});
        }
    }, [isMobile, selectedId, filtered, navigate]);

    // ── Fetch the selected conversation context + messages ───────────────
    const {data: msgDetails, isLoading: detailsLoading}: UseQueryResult<IMessage, Error> = useQuery({
        queryKey: ["candidate-det", selectedId],
        queryFn: () => getCandidateFromMsgByIdApi(String(selectedId)),
        enabled: !!selectedId,
    });

    const {data: thread, isLoading: threadLoading}: UseQueryResult<IMessageBuble[], Error> = useQuery({
        queryKey: ["message", msgDetails?.id],
        queryFn: () => getCandidateMessagesByComId(msgDetails!.id),
        enabled: !!msgDetails?.id,
    });

    const sendMessageMutation = useMutation({
        mutationFn: sendMessageApi,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["message", msgDetails!.id]});
            setReply("");
        },
    });

    const hideMutation = useMutation({
        mutationFn: ({id, hidden}: {id: number; hidden: boolean}) =>
            setConversationHidden(id, hidden),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: ["messages"]});
            queryClient.invalidateQueries({queryKey: ["candidate-det", selectedId]});
            if (variables.hidden && !showHidden) {
                setSelectedId(null);
                navigate("/messages", {replace: true});
            }
        },
    });

    // Combined chat list: original first message + bubbles (sorted by time)
    const messageList: CombinedMessageType[] = useMemo(() => {
        if (!msgDetails) return [];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {_client: _c, _job_demand_with_labels: _j, ...firstMessage} = msgDetails;
        const bubbles = thread ?? [];
        return [...bubbles, firstMessage].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
    }, [msgDetails, thread]);

    // ── Handlers ─────────────────────────────────────────────────────────
    const handleSelect = useCallback(
        (item: IMessage) => {
            setSelectedId(item.id);
            navigate(`/messages/${item.id}`, {replace: true});
        },
        [navigate],
    );

    const handleBack = useCallback(() => {
        setSelectedId(null);
        navigate("/messages", {replace: true});
    }, [navigate]);

    const handleSend = useCallback(() => {
        if (!reply.trim() || !msgDetails) return;
        sendMessageMutation.mutate({
            message: reply,
            type: "outreach",
            client_id: msgDetails.client_id,
            candidate_id: msgDetails.candidate_id,
            job_demand_id: msgDetails._job_demand_with_labels[0]?.id || 0,
            communication_id: msgDetails.id,
            contact_data: msgDetails.contact_data,
        });
    }, [reply, msgDetails, sendMessageMutation]);

    // ── Virtualized list ─────────────────────────────────────────────────
    const itemCount = hasNextPage ? filtered.length + 1 : filtered.length;
    const loadMoreItems = useCallback(() => {
        if (isFetchingNextPage) return Promise.resolve();
        return fetchNextPage().then(() => {});
    }, [isFetchingNextPage, fetchNextPage]);
    const isItemLoaded = useCallback(
        (index: number) => !hasNextPage || index < filtered.length,
        [hasNextPage, filtered.length],
    );

    // ── Scroll thread to bottom on update ────────────────────────────────
    const threadEndRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        threadEndRef.current?.scrollIntoView({behavior: "smooth"});
    }, [messageList.length]);

    // ── Computed display ─────────────────────────────────────────────────
    const employer = msgDetails?._client?.profile_name || "";
    const jobTitle = msgDetails?._job_demand_with_labels?.[0]?.title || "";
    const status = deriveStatus(msgDetails);
    const statusCfg = STATUS_CONFIG[status];
    const job = msgDetails?._job_demand_with_labels?.[0];
    const isHidden = !!msgDetails?.candidate_hidden;

    const statusCounts = useMemo(() => {
        const out: Record<string, number> = {all: flattenedData.length};
        flattenedData.forEach((m) => {
            const s = deriveStatus(m);
            out[s] = (out[s] || 0) + 1;
        });
        return out;
    }, [flattenedData]);

    const showThread = !!selectedId;

    // ── Render ───────────────────────────────────────────────────────────
    return (
        <Outer>
            {isFetching && !flattenedData.length && (
                <AbsLoader>
                    <Spin size="large"/>
                </AbsLoader>
            )}

            {/* LEFT: Conversation List */}
            {(!isMobile || !showThread) && (
                <Sidebar>
                    <SidebarHeader>
                        <HeaderRow>
                            <Title level={4} style={{margin: 0}}>
                                {showHidden ? "Ausgeblendete" : "Nachrichten"}
                            </Title>
                            {flattenedData.length > 0 && (
                                <Badge
                                    count={flattenedData.length}
                                    style={{backgroundColor: colors.main}}
                                />
                            )}
                        </HeaderRow>
                        <Input
                            placeholder="Arbeitgeber oder Stelle suchen…"
                            prefix={<SearchOutlined style={{color: "#bfbfbf"}}/>}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            allowClear
                            style={{marginBottom: 10}}
                        />
                        <FilterRow>
                            <Tooltip title={sortOrder === "newest" ? "Neueste zuerst" : "Älteste zuerst"}>
                                <Button
                                    icon={sortOrder === "newest" ? <SortDescendingOutlined/> : <SortAscendingOutlined/>}
                                    onClick={() => setSortOrder((p) => (p === "newest" ? "oldest" : "newest"))}
                                />
                            </Tooltip>
                            <Tooltip title={showHidden ? "Aktive Bewerbungen anzeigen" : "Ausgeblendete anzeigen"}>
                                <Button
                                    type={showHidden ? "primary" : "default"}
                                    icon={showHidden ? <EyeOutlined/> : <EyeInvisibleOutlined/>}
                                    onClick={() => {
                                        setShowHidden((p) => !p);
                                        setSelectedId(null);
                                        navigate("/messages", {replace: true});
                                    }}
                                />
                            </Tooltip>
                        </FilterRow>
                        <TabsRow>
                            {STATUS_TABS.map((tab) => {
                                const active = statusFilter === tab.key;
                                const count = tab.key === "all" ? statusCounts.all : statusCounts[tab.key] || 0;
                                return (
                                    <TabButton
                                        key={tab.key}
                                        $active={active}
                                        onClick={() => setStatusFilter(tab.key)}
                                    >
                                        {tab.label}
                                        {count > 0 && <TabCount>{count}</TabCount>}
                                    </TabButton>
                                );
                            })}
                        </TabsRow>
                    </SidebarHeader>
                    <ListArea>
                        {filtered.length === 0 ? (
                            <EmptyList>
                                <Text type="secondary">
                                    {showHidden
                                        ? "Keine ausgeblendeten Bewerbungen"
                                        : "Keine Bewerbungen gefunden"}
                                </Text>
                            </EmptyList>
                        ) : (
                            <InfiniteLoader
                                isItemLoaded={isItemLoaded}
                                itemCount={itemCount}
                                loadMoreItems={loadMoreItems}
                            >
                                {({onItemsRendered, ref}) => (
                                    <VirtualList
                                        height={window.innerHeight - 260}
                                        itemCount={itemCount}
                                        itemSize={ROW_HEIGHT}
                                        onItemsRendered={onItemsRendered}
                                        ref={(instance) => {
                                            ref(instance);
                                            listRef.current = instance;
                                        }}
                                        width="100%"
                                    >
                                        {({index, style}) => {
                                            const item = filtered[index];
                                            if (!item) {
                                                return (
                                                    <div style={style}>
                                                        <RowSpinner><Spin size="small"/></RowSpinner>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div style={style}>
                                                    <MessageItem
                                                        item={item}
                                                        selected={item.id === selectedId}
                                                        onSelect={handleSelect}
                                                        onToggleHidden={(m) =>
                                                            hideMutation.mutate({
                                                                id: m.id,
                                                                hidden: !m.candidate_hidden,
                                                            })
                                                        }
                                                        toggleHiddenLoading={
                                                            hideMutation.isPending &&
                                                            hideMutation.variables?.id === item.id
                                                        }
                                                    />
                                                </div>
                                            );
                                        }}
                                    </VirtualList>
                                )}
                            </InfiniteLoader>
                        )}
                    </ListArea>
                </Sidebar>
            )}

            {/* CENTER: Thread */}
            {(!isMobile || showThread) && (
                <Center>
                    {!selectedId ? (
                        <EmptyState>
                            <InboxOutlined style={{fontSize: 64, marginBottom: 16, color: "#bfbfbf"}}/>
                            <Title level={4} type="secondary">Wählen Sie eine Bewerbung aus</Title>
                            <Text type="secondary">
                                Klicken Sie links auf eine Bewerbung, um den Gesprächsverlauf zu sehen.
                            </Text>
                        </EmptyState>
                    ) : detailsLoading || !msgDetails ? (
                        <CenterLoader><Spin size="large"/></CenterLoader>
                    ) : (
                        <>
                            <ChatHeader>
                                <ChatHeaderLeft>
                                    {isMobile && (
                                        <Button
                                            type="text"
                                            icon={<ArrowLeftOutlined/>}
                                            onClick={handleBack}
                                            style={{marginRight: 4}}
                                        />
                                    )}
                                    <Avatar
                                        size={36}
                                        style={{backgroundColor: colors.main, fontSize: 14, fontWeight: 600, flexShrink: 0}}
                                    >
                                        {getInitials(employer)}
                                    </Avatar>
                                    <ChatHeaderTitles>
                                        <Text strong style={{fontSize: 15}} ellipsis>{employer}</Text>
                                        <Text type="secondary" style={{fontSize: 12}} ellipsis>{jobTitle}</Text>
                                    </ChatHeaderTitles>
                                </ChatHeaderLeft>
                                <Space>
                                    <Chip $bg={statusCfg.bg} $color={statusCfg.color}>{statusCfg.label}</Chip>
                                    <Tooltip title={isHidden ? "Wiederherstellen" : "Konversation ausblenden"}>
                                        <Button
                                            size="small"
                                            icon={isHidden ? <EyeOutlined/> : <EyeInvisibleOutlined/>}
                                            loading={hideMutation.isPending}
                                            onClick={() =>
                                                msgDetails &&
                                                hideMutation.mutate({id: msgDetails.id, hidden: !isHidden})
                                            }
                                        />
                                    </Tooltip>
                                    {!isMobile && (
                                        <Tooltip title={showInfo ? "Stellendetails ausblenden" : "Stellendetails einblenden"}>
                                            <Button
                                                type={showInfo ? "primary" : "default"}
                                                icon={<BankOutlined/>}
                                                size="small"
                                                onClick={() => setShowInfo((p) => !p)}
                                            />
                                        </Tooltip>
                                    )}
                                </Space>
                            </ChatHeader>

                            <MessagesArea>
                                {threadLoading && <CenterLoader><Spin/></CenterLoader>}
                                {messageList.map((msg, idx) => {
                                    const outgoing =
                                        ("sending_users_id" in msg && msg.sending_users_id === user?.id) ||
                                        msg.type === "application";
                                    return (
                                        <BubbleRow key={("id" in msg ? msg.id : idx)} $outgoing={outgoing}>
                                            <Bubble $outgoing={outgoing}>
                                                <BubbleText $outgoing={outgoing}>{msg.message}</BubbleText>
                                                <BubbleTime $outgoing={outgoing}>
                                                    {formatDistanceToNow(new Date(msg.created_at), {
                                                        addSuffix: true,
                                                        locale: de,
                                                    })}
                                                </BubbleTime>
                                            </Bubble>
                                        </BubbleRow>
                                    );
                                })}
                                <div ref={threadEndRef}/>
                            </MessagesArea>

                            <Composer>
                                <TextArea
                                    placeholder="Nachricht schreiben…"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    autoSize={{minRows: 1, maxRows: 5}}
                                    style={{flex: 1, borderRadius: 20, resize: "none"}}
                                />
                                <Button
                                    type="primary"
                                    icon={<SendOutlined/>}
                                    shape="circle"
                                    size="large"
                                    onClick={handleSend}
                                    disabled={!reply.trim() || sendMessageMutation.isPending}
                                    loading={sendMessageMutation.isPending}
                                />
                            </Composer>
                        </>
                    )}
                </Center>
            )}

            {/* RIGHT: Info Panel */}
            {!isMobile && showInfo && msgDetails && (
                <InfoPanel>
                    <Title level={5} style={{marginBottom: 16}}>Stellendetails</Title>
                    <InfoBlock>
                        <Text strong style={{fontSize: 14, display: "block", marginBottom: 8}}>
                            {jobTitle || "Keine Stelle"}
                        </Text>
                        <Space direction="vertical" size={4} style={{width: "100%"}}>
                            {employer && (
                                <Text type="secondary" style={{fontSize: 13}}>
                                    <BankOutlined style={{marginRight: 6}}/>
                                    {employer}
                                </Text>
                            )}
                            {job?.search_location_label && (
                                <Text type="secondary" style={{fontSize: 13}}>
                                    <EnvironmentOutlined style={{marginRight: 6}}/>
                                    {job.search_location_label}
                                </Text>
                            )}
                            {job?.job_type_label && (
                                <Text type="secondary" style={{fontSize: 13}}>
                                    <ClockCircleOutlined style={{marginRight: 6}}/>
                                    {job.job_type_label}
                                </Text>
                            )}
                        </Space>
                    </InfoBlock>

                    <InfoBlock>
                        <Text type="secondary" style={{fontSize: 12, display: "block", marginBottom: 4}}>
                            Bewerbungsstatus
                        </Text>
                        <Chip $bg={statusCfg.bg} $color={statusCfg.color}>{statusCfg.label}</Chip>
                    </InfoBlock>

                    {msgDetails.created_at && (
                        <InfoBlock>
                            <Text type="secondary" style={{fontSize: 12, display: "block", marginBottom: 4}}>
                                Beworben am
                            </Text>
                            <Text style={{fontSize: 13}}>
                                {new Date(msgDetails.created_at).toLocaleDateString("de-DE", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </Text>
                        </InfoBlock>
                    )}

                    {job?.id && (
                        <Button
                            icon={<BankOutlined/>}
                            block
                            style={{marginTop: 8}}
                            onClick={() => navigate(`/jobs/${job.id}`)}
                        >
                            Zur Stelle
                        </Button>
                    )}
                </InfoPanel>
            )}
        </Outer>
    );
};

// ── Styled ───────────────────────────────────────────────────────────────
const Outer = styled.div`
    display: flex;
    height: calc(100vh - 80px);
    background: #fff;
    border-radius: 10px;
    overflow: hidden;

    @media (max-width: 767px) {
        height: calc(100vh - 60px);
        border-radius: 0;
    }
`;

const Sidebar = styled.div`
    width: ${SIDEBAR_WIDTH}px;
    border-right: 1px solid #f0f0f0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    background: #fff;

    @media (max-width: 767px) {
        width: 100%;
    }
`;

const SidebarHeader = styled.div`
    padding: 16px 16px 0;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

const FilterRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 10px;
`;

const TabsRow = styled.div`
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    padding-bottom: 12px;
`;

const TabButton = styled.button<{$active: boolean}>`
    padding: 4px 12px;
    border-radius: 16px;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    background: ${(p) => (p.$active ? colors.main : "#f5f5f5")};
    color: ${(p) => (p.$active ? "#fff" : "#595959")};
    transition: background 0.15s ease;
`;

const TabCount = styled.span`
    margin-left: 4px;
    opacity: 0.7;
`;

const ListArea = styled.div`
    flex: 1;
    overflow-y: auto;

    &::-webkit-scrollbar { display: none; }
`;

const EmptyList = styled.div`
    text-align: center;
    padding: 40px 16px;
`;

const RowSpinner = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
`;

const Center = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: #fff;
`;

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #bfbfbf;
    text-align: center;
    padding: 24px;
`;

const CenterLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
`;

const ChatHeader = styled.div`
    padding: 12px 20px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
`;

const ChatHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const ChatHeaderTitles = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const MessagesArea = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #f5f5f5;
`;

const BubbleRow = styled.div<{$outgoing: boolean}>`
    display: flex;
    justify-content: ${(p) => (p.$outgoing ? "flex-end" : "flex-start")};
    margin-bottom: 16px;
`;

const Bubble = styled.div<{$outgoing: boolean}>`
    max-width: 70%;
    padding: 12px 16px;
    border-radius: ${(p) => (p.$outgoing ? "16px 16px 4px 16px" : "16px 16px 16px 4px")};
    background: ${(p) => (p.$outgoing ? colors.main : "#fff")};
    color: ${(p) => (p.$outgoing ? "#fff" : "#262626")};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

    @media (max-width: 767px) {
        max-width: 85%;
    }
`;

const BubbleText = styled.p<{$outgoing: boolean}>`
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: ${(p) => (p.$outgoing ? "#fff" : "#262626")};
    white-space: pre-wrap;
    word-break: break-word;
`;

const BubbleTime = styled.div<{$outgoing: boolean}>`
    text-align: right;
    margin-top: 6px;
    font-size: 11px;
    color: ${(p) => (p.$outgoing ? "rgba(255,255,255,0.8)" : "#8c8c8c")};
`;

const Composer = styled.div`
    padding: 12px 20px;
    border-top: 1px solid #f0f0f0;
    background: #fff;
    display: flex;
    gap: 12px;
    align-items: flex-end;
`;

const InfoPanel = styled.div`
    width: ${INFO_PANEL_WIDTH}px;
    border-left: 1px solid #f0f0f0;
    background: #fafafa;
    padding: 20px 16px;
    overflow-y: auto;
    flex-shrink: 0;
`;

const InfoBlock = styled.div`
    margin-bottom: 20px;
`;

const Chip = styled.span<{$bg: string; $color: string}>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    line-height: 20px;
    padding: 0 10px;
    border-radius: 4px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    border: 1px solid ${(p) => p.$color}20;
    white-space: nowrap;
`;
